import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { TrendingUp, Sun, Users, Plus, Trash2, CalendarRange } from 'lucide-react';
import { toast } from 'sonner';

export default function DynamicPricing() {
  const [seasons, setSeasons] = useState([]);
  const [occRules, setOccRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seasonModal, setSeasonModal] = useState(false);
  const [ruleModal, setRuleModal] = useState(false);
  const [seasonForm, setSeasonForm] = useState({ name: '', start_date: '', end_date: '', multiplier: 1.0, active: true });
  const [ruleForm, setRuleForm] = useState({ threshold_percent: 70, multiplier: 1.15, active: true });

  const loadData = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([api.get('/pricing/seasons'), api.get('/pricing/occupancy-rules')]);
      setSeasons(s.data);
      setOccRules(r.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const addSeason = async (e) => {
    e.preventDefault();
    try {
      await api.post('/pricing/seasons', { ...seasonForm, multiplier: parseFloat(seasonForm.multiplier) });
      toast.success('Season added');
      setSeasonModal(false);
      setSeasonForm({ name: '', start_date: '', end_date: '', multiplier: 1.0, active: true });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const addRule = async (e) => {
    e.preventDefault();
    try {
      await api.post('/pricing/occupancy-rules', { ...ruleForm, threshold_percent: parseInt(ruleForm.threshold_percent), multiplier: parseFloat(ruleForm.multiplier) });
      toast.success('Rule added');
      setRuleModal(false);
      setRuleForm({ threshold_percent: 70, multiplier: 1.15, active: true });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteSeason = async (id) => {
    try { await api.delete(`/pricing/seasons/${id}`); toast.success('Deleted'); loadData(); }
    catch { toast.error('Failed'); }
  };

  const deleteRule = async (id) => {
    try { await api.delete(`/pricing/occupancy-rules/${id}`); toast.success('Deleted'); loadData(); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div></div>;

  return (
    <div data-testid="dynamic-pricing-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-900 font-['Manrope']">Dynamic Pricing</h1>
        <p className="text-stone-500 mt-1">Seasonal and occupancy-based rate adjustments</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Seasonal Pricing */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-stone-900 font-['Manrope'] flex items-center gap-2"><Sun className="w-5 h-5 text-amber-600" />Seasonal Pricing</h2>
            <Button onClick={() => setSeasonModal(true)} size="sm" className="bg-sage-700 hover:bg-sage-800 text-white" data-testid="add-season-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add Season</Button>
          </div>
          {seasons.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-md p-8 text-center">
              <CalendarRange className="w-10 h-10 mx-auto mb-2 text-stone-300" />
              <p className="text-sm text-stone-500">No seasons configured</p>
              <p className="text-xs text-stone-400 mt-1">Add peak/off-peak seasons to auto-adjust rates</p>
            </div>
          ) : (
            <div className="space-y-2">
              {seasons.map(s => (
                <div key={s.id} className={`bg-white border rounded-md p-4 flex items-center justify-between ${s.active ? 'border-stone-200' : 'border-stone-100 opacity-60'}`} data-testid={`season-${s.id}`}>
                  <div>
                    <p className="font-medium text-stone-900">{s.name}</p>
                    <p className="text-sm text-stone-500">{s.start_date} → {s.end_date}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`font-bold text-lg ${s.multiplier > 1 ? 'text-red-600' : s.multiplier < 1 ? 'text-emerald-600' : 'text-stone-600'}`}>
                      {s.multiplier > 1 ? '+' : ''}{Math.round((s.multiplier - 1) * 100)}%
                    </span>
                    <button onClick={() => deleteSeason(s.id)} className="text-stone-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Occupancy Rules */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-stone-900 font-['Manrope'] flex items-center gap-2"><Users className="w-5 h-5 text-blue-600" />Occupancy Rules</h2>
            <Button onClick={() => setRuleModal(true)} size="sm" className="bg-sage-700 hover:bg-sage-800 text-white" data-testid="add-rule-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add Rule</Button>
          </div>
          {occRules.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-md p-8 text-center">
              <TrendingUp className="w-10 h-10 mx-auto mb-2 text-stone-300" />
              <p className="text-sm text-stone-500">No occupancy rules configured</p>
              <p className="text-xs text-stone-400 mt-1">Auto-increase rates when occupancy is high</p>
            </div>
          ) : (
            <div className="space-y-2">
              {occRules.map(r => (
                <div key={r.id} className={`bg-white border rounded-md p-4 flex items-center justify-between ${r.active ? 'border-stone-200' : 'border-stone-100 opacity-60'}`} data-testid={`rule-${r.id}`}>
                  <div>
                    <p className="font-medium text-stone-900">When occupancy reaches {r.threshold_percent}%</p>
                    <p className="text-sm text-stone-500">Increase rates by {Math.round((r.multiplier - 1) * 100)}%</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-lg text-red-600">×{r.multiplier}</span>
                    <button onClick={() => deleteRule(r.id)} className="text-stone-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="mt-8 bg-stone-50 border border-stone-200 rounded-md p-4">
        <h3 className="font-medium text-stone-700 mb-2">How Dynamic Pricing Works</h3>
        <ul className="text-sm text-stone-500 space-y-1">
          <li>Base rate is set per room/bed in Room/Bed management</li>
          <li>Seasonal multipliers adjust rates during defined periods</li>
          <li>Occupancy multipliers increase rates when demand is high</li>
          <li>Both multipliers stack: Effective Rate = Base × Season × Occupancy</li>
          <li>GST is calculated on the final effective rate (12% below ₹7,500, 18% above)</li>
        </ul>
      </div>

      {/* Season Modal */}
      <Dialog open={seasonModal} onOpenChange={setSeasonModal}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Manrope']">Add Season</DialogTitle></DialogHeader>
          <form onSubmit={addSeason} className="space-y-3 mt-4">
            <div className="space-y-1"><Label>Name</Label><Input value={seasonForm.name} onChange={e => setSeasonForm({...seasonForm, name: e.target.value})} placeholder="Peak Tourist Season" required className="border-stone-300" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Start (MM-DD or YYYY-MM-DD)</Label><Input value={seasonForm.start_date} onChange={e => setSeasonForm({...seasonForm, start_date: e.target.value})} placeholder="10-01" required className="border-stone-300" /></div>
              <div className="space-y-1"><Label>End</Label><Input value={seasonForm.end_date} onChange={e => setSeasonForm({...seasonForm, end_date: e.target.value})} placeholder="12-31" required className="border-stone-300" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Multiplier (1.5 = +50%)</Label><Input type="number" min="0.1" max="5" step="0.05" value={seasonForm.multiplier} onChange={e => setSeasonForm({...seasonForm, multiplier: e.target.value})} required className="border-stone-300" /></div>
              <div className="flex items-center gap-2 pt-6"><Switch checked={seasonForm.active} onCheckedChange={v => setSeasonForm({...seasonForm, active: v})} className="data-[state=checked]:bg-emerald-700" /><Label>Active</Label></div>
            </div>
            <div className="flex justify-end gap-3 pt-3">
              <Button type="button" variant="outline" onClick={() => setSeasonModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-sage-700 hover:bg-sage-800 text-white">Add Season</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rule Modal */}
      <Dialog open={ruleModal} onOpenChange={setRuleModal}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Manrope']">Add Occupancy Rule</DialogTitle></DialogHeader>
          <form onSubmit={addRule} className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Occupancy Threshold (%)</Label><Input type="number" min="10" max="100" value={ruleForm.threshold_percent} onChange={e => setRuleForm({...ruleForm, threshold_percent: e.target.value})} required className="border-stone-300" /></div>
              <div className="space-y-1"><Label>Multiplier (1.15 = +15%)</Label><Input type="number" min="1" max="5" step="0.05" value={ruleForm.multiplier} onChange={e => setRuleForm({...ruleForm, multiplier: e.target.value})} required className="border-stone-300" /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={ruleForm.active} onCheckedChange={v => setRuleForm({...ruleForm, active: v})} className="data-[state=checked]:bg-emerald-700" /><Label>Active</Label></div>
            <div className="flex justify-end gap-3 pt-3">
              <Button type="button" variant="outline" onClick={() => setRuleModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-sage-700 hover:bg-sage-800 text-white">Add Rule</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
